import { create } from 'zustand';
import { NO_PERMISSIONS } from '../permissions/session-permissions';
import type { AuthSessionState } from '../type';

/**
 * The signed-in session, as client state.
 *
 * Deliberately NOT persisted: Amplify already owns token storage, and a second copy in
 * `localStorage` would go stale the moment Amplify refreshes. This store is a synchronous,
 * render-friendly mirror of what `token.service` last resolved — `bootstrapAuth()` fills it
 * once at startup and the guards read it.
 *
 * ### Two statuses, not one
 *
 * `status` answers "is anyone signed in", `permissionStatus` answers "do we know what they may
 * do". They resolve at different times: the session comes from Amplify locally, the grant comes
 * from `GET /rf-backend/staff-permissions` over the network. Collapsing them into one flag
 * means either the shell renders before permissions arrive — flashing every gated screen — or
 * it waits on the network to decide whether to show a sign-in page it already knows it needs.
 *
 * Both start `'unknown'`, meaning "not checked yet", which is what stops a guard bouncing a
 * signed-in user to the sign-in page on the first frame of a hard refresh.
 */
export const useAuthSessionStore = create<AuthSessionState>((set) => ({
  status: 'unknown',
  tokens: null,
  claims: null,
  permissions: NO_PERMISSIONS,
  permissionStatus: 'unknown',

  /**
   * A session arriving from signed-out does **not** know its permissions yet.
   *
   * `setUnauthenticated` leaves `permissionStatus` at `'loaded'` with an empty grant, and carrying
   * that across a sign-in made `usePermission().isReady` true while the real grant was still in
   * flight. `HomeRedirect` then picked a landing screen from the empty/stale grant, rendered it
   * with a partial sidebar, and redirected again when the grant arrived. A refresh of the same
   * session (already authenticated) keeps what it has, so the sidebar does not blink.
   */
  setSession: (tokens, claims) =>
    set((state) =>
      state.status === 'authenticated'
        ? { tokens, claims }
        : {
            status: 'authenticated',
            tokens,
            claims,
            permissions: NO_PERMISSIONS,
            permissionStatus: 'unknown',
          }
    ),

  setPermissions: (permissions) => set({ permissions, permissionStatus: 'loaded' }),

  clearSession: () =>
    set({
      status: 'unauthenticated',
      tokens: null,
      claims: null,
      permissions: NO_PERMISSIONS,
      permissionStatus: 'unknown',
    }),

  setUnauthenticated: () =>
    set({
      status: 'unauthenticated',
      tokens: null,
      claims: null,
      permissions: NO_PERMISSIONS,
      // Nothing to load for a signed-out session, and leaving this `'unknown'` would leave
      // every permission check waiting forever on a fetch that is never going to happen.
      permissionStatus: 'loaded',
    }),
}));
